<div class="prompt-tracking-container">

  <!-- Loading State -->
  @if (loading || initializing) {
    <div class="loading-container">
      <mat-spinner diameter="50"></mat-spinner>
      <p>{{ initializing ? 'Performing initial analysis...' : 'Loading tracking data...' }}</p>
      
      <!-- Task Progress (if task is running) -->
      @if (taskStatus && (taskStatus.status === 'progress' || taskStatus.status === 'pending')) {
        <div class="task-progress">
          @if (taskStatus.prompt_source) {
            <p class="prompt-source-label">Prompts: {{ getPromptSourceLabel(taskStatus.prompt_source) }}</p>
          }
          <div class="progress-info">
            <p class="current-step">{{ currentStep || (taskStatus.status === 'pending' ? 'Initializing...' : 'Processing...') }}</p>
            <p class="progress-text">
              @if (taskStatus.total_steps != null) {
                @if (taskStatus.total_prompts > 0) {
                  {{ taskStatus.completed_prompts || 0 }} / {{ taskStatus.total_prompts }} prompts processed
                } @else {
                  Step {{ taskStatus.completed_steps || 0 }} / {{ taskStatus.total_steps }}
                }
                ({{ progressPercentage.toFixed(0) }}%)
              } @else {
                Determining prompts…
              }
            </p>
          </div>
          <mat-progress-bar 
            mode="determinate" 
            [value]="progressPercentage"
            class="progress-bar">
          </mat-progress-bar>
          @if (taskStatus.estimated_time_remaining) {
            <p class="estimated-time">
              Estimated time remaining: {{ formatEstimatedTime(taskStatus.estimated_time_remaining) }}
            </p>
          }
          <!-- Stop/Cancel Button -->
          <div class="task-actions">
            <button 
              mat-raised-button 
              color="warn" 
              (click)="cancelTask()"
              [disabled]="!currentTaskId">
              <mat-icon>stop</mat-icon>
              Stop Task
            </button>
          </div>
        </div>
      }
    </div>
  }

  <!-- Error State -->
  @if (error && !loading && !initializing) {
    <div class="error-container">
      <mat-icon class="error-icon">error_outline</mat-icon>
      <p class="error-message">{{ error }}</p>
      <button mat-button color="primary" (click)="loadData()">Retry</button>
    </div>
  }

  <!-- Main Content -->
  @if (!loading && !initializing && !error) {
    <!-- Prompt Tracking Header -->
    <div class="active-tab-info">
      <h2>Prompt Tracking</h2>
      <div class="header-actions">
        <button
          mat-button
          color="primary"
          (click)="openPromptTrackingSourceDialog()"
          [disabled]="loading || initializing">
          Setup Prompt Tracking Analysis
        </button>
        <button mat-icon-button [matMenuTriggerFor]="tabMenu" matTooltip="Open tab menu" class="mobile-tab-menu">
          <mat-icon>menu</mat-icon>
        </button>
      </div>
    </div>

    <!-- Tab Selection Menu -->
    <mat-menu #tabMenu="matMenu">
      @for (tab of getVisibleTabs(); track tab.id) {
        @if (tab.subTabs && tab.subTabs.length > 0) {
          <!-- Parent Tab with Sub-menu -->
          <button mat-menu-item [matMenuTriggerFor]="auditSubMenu">
            <mat-icon>{{ tab.icon }}</mat-icon>
            <span>{{ tab.name }}</span>
            <mat-icon>arrow_right</mat-icon>
          </button>
        } @else {
          <button mat-menu-item (click)="openTab(tab.id)">
            <mat-icon>{{ tab.icon }}</mat-icon>
            <span>{{ tab.name }}</span>
          </button>
        }
      }
    </mat-menu>
    
    <!-- Sub-menu for Audit tab -->
    <mat-menu #auditSubMenu="matMenu">
      @for (subTab of getSubTabs('audit'); track subTab.id) {
        <button mat-menu-item (click)="openSubTab('audit', subTab.id)">
          <mat-icon>{{ subTab.icon }}</mat-icon>
          <span>{{ subTab.name }}</span>
        </button>
      }
    </mat-menu>

    <!-- Empty state when no data: open same dialog as "Run analysis" -->
    <!-- @if (prompts.length === 0 && !loading && !initializing) {
      <div class="empty-state">
        <p>No prompt tracking data yet.</p>
        <button mat-raised-button color="primary" (click)="openPromptTrackingSourceDialog()">Get started</button>
      </div>
    } -->

    <!-- First Section: Composite Score & Growth Chart -->
    @if (prompts.length > 0) {
      <div class="top-section">
        
        <!-- Composite Score Widget -->
        <!-- <div class="score-widget-container">
          <app-composite-score-widget 
            [compositeScore]="compositeScore?.score || 0" 
            [title]="'Prompt Tracking Score'" 
            [size]="'medium'">
          </app-composite-score-widget>
        </div> -->



        <!-- Growth Chart with Tabs -->
        <div class="chart-container">
          <div class="chart-header">
            <div class="chart-title-section">
              <h4 class="chart-title">Prompt Tracking Growth Analysis</h4>
              <p class="chart-subtitle">{{ getMetricLabel(selectedMetricTab) }} trends over time</p>
            </div>
            <div class="chart-controls">
              <mat-form-field appearance="outline" class="filter-field">
                <mat-label>Time Range</mat-label>
                <mat-select [(ngModel)]="dateFilter" (selectionChange)="onDateFilterChange()">
                  <mat-option value="monthly">Monthly</mat-option>
                  <mat-option value="quarterly">Quarterly</mat-option>
                  <mat-option value="semi-annual">Semi-annual</mat-option>
                  <mat-option value="annual">Annual</mat-option>
                  <!-- <mat-option value="custom">Custom</mat-option> -->
                </mat-select>
              </mat-form-field>
              @if (dateFilter === 'custom') {
                <mat-form-field appearance="outline" class="date-field">
                  <mat-label>Start Date</mat-label>
                  <input matInput [matDatepicker]="startPicker" [(ngModel)]="customStartDate" (dateChange)="onDateFilterChange()">
                  <mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
                  <mat-datepicker #startPicker></mat-datepicker>
                  @if (customStartDate && customEndDate && !isDateRangeValid) {
                    <mat-error>Start date must be before end date</mat-error>
                  }
                </mat-form-field>
                <mat-form-field appearance="outline" class="date-field">
                  <mat-label>End Date</mat-label>
                  <input matInput [matDatepicker]="endPicker" [(ngModel)]="customEndDate" [min]="customStartDate || null" (dateChange)="onDateFilterChange()">
                  <mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
                  <mat-datepicker #endPicker></mat-datepicker>
                  @if (customStartDate && customEndDate && !isDateRangeValid) {
                    <mat-error>End date must be after start date</mat-error>
                  }
                </mat-form-field>
                <button mat-raised-button color="primary" (click)="onDateFilterChange()" [disabled]="!isDateRangeValid">
                  Search
                </button>
              }
              <button mat-icon-button (click)="exportData()" matTooltip="Export Data">
                <mat-icon>download</mat-icon>
              </button>
            </div>
          </div>

          <!-- Metric Tabs -->
          <mat-tab-group (selectedTabChange)="onMetricTabChange($event)">
            <mat-tab label="Mentions">
              <div class="chart-wrapper">
                @if (chartData.length > 0) {
                  <app-bar-line-combo-chart
                    [view]="view"
                    [scheme]="comboBarScheme"
                    [colorSchemeLine]="lineChartScheme"
                    [results]="chartData"
                    [lineChart]="lineChart"
                    [animations]="true"
                    [tooltipDisabled]="false"
                    [gradient]="false"
                    [xAxis]="true"
                    [yAxis]="true"
                    [legend]="false"
                    [showGridLines]="true"
                    [showXAxisLabel]="true"
                    [showYAxisLabel]="true"
                    [xAxisLabel]="getXAxisLabel()"
                    [yAxisLabel]="'Mentions'">
                  </app-bar-line-combo-chart>
                } @else {
                  <div class="no-chart-data">No data available for Mentions</div>
                }
              </div>
            </mat-tab>
            <mat-tab label="Citations">
              <div class="chart-wrapper">
                @if (chartData.length > 0) {
                  <app-bar-line-combo-chart
                    [view]="view"
                    [scheme]="comboBarScheme"
                    [colorSchemeLine]="lineChartScheme"
                    [results]="chartData"
                    [lineChart]="lineChart"
                    [animations]="true"
                    [tooltipDisabled]="false"
                    [gradient]="false"
                    [xAxis]="true"
                    [yAxis]="true"
                    [legend]="false"
                    [showGridLines]="true"
                    [showXAxisLabel]="true"
                    [showYAxisLabel]="true"
                    [xAxisLabel]="getXAxisLabel()"
                    [yAxisLabel]="'Citations'">
                  </app-bar-line-combo-chart>
                } @else {
                  <div class="no-chart-data">No data available for Citations</div>
                }
              </div>
            </mat-tab>
            <mat-tab label="Impressions">
              <div class="chart-wrapper">
                @if (chartData.length > 0) {
                  <app-bar-line-combo-chart
                    [view]="view"
                    [scheme]="comboBarScheme"
                    [colorSchemeLine]="lineChartScheme"
                    [results]="chartData"
                    [lineChart]="lineChart"
                    [animations]="true"
                    [tooltipDisabled]="false"
                    [gradient]="false"
                    [xAxis]="true"
                    [yAxis]="true"
                    [legend]="false"
                    [showGridLines]="true"
                    [showXAxisLabel]="true"
                    [showYAxisLabel]="true"
                    [xAxisLabel]="getXAxisLabel()"
                    [yAxisLabel]="'Impressions'">
                  </app-bar-line-combo-chart>
                } @else {
                  <div class="no-chart-data">No data available for Impressions</div>
                }
              </div>
            </mat-tab>
            <mat-tab label="AI Share of Voice">
              <div class="chart-wrapper">
                @if (chartData.length > 0) {
                  <app-bar-line-combo-chart
                    [view]="view"
                    [scheme]="comboBarScheme"
                    [colorSchemeLine]="lineChartScheme"
                    [results]="chartData"
                    [lineChart]="lineChart"
                    [animations]="true"
                    [tooltipDisabled]="false"
                    [gradient]="false"
                    [xAxis]="true"
                    [yAxis]="true"
                    [legend]="false"
                    [showGridLines]="true"
                    [showXAxisLabel]="true"
                    [showYAxisLabel]="true"
                    [xAxisLabel]="getXAxisLabel()"
                    [yAxisLabel]="'AI Share of Voice (%)'">
                  </app-bar-line-combo-chart>
                } @else {
                  <div class="no-chart-data">No data available for AI Share of Voice</div>
                }
              </div>
            </mat-tab>
          </mat-tab-group>
        </div>
      </div>
    }

    <!-- Second Section: Prompt List Table -->
    @if (prompts.length > 0) {
      <div class="prompt-list-section">
        <div class="table-header">
          <div class="table-info">
            <span class="result-count">{{ totalResults.toLocaleString() }} results</span>
            <mat-form-field appearance="outline" class="sort-field">
              <mat-label>Sort by</mat-label>
              <mat-select [(ngModel)]="sortBy" (selectionChange)="onSortChange()">
                <mat-option value="relevance">By relevance</mat-option>
                <mat-option value="date">By date</mat-option>
                <mat-option value="mentions">By mentions</mat-option>
                <mat-option value="volume">By volume</mat-option>
              </mat-select>
            </mat-form-field>
            <button mat-icon-button (click)="exportData()" matTooltip="Export to CSV">
              <mat-icon>download</mat-icon>
            </button>
          </div>
        </div>

        <table mat-table [dataSource]="getPaginatedPrompts()" class="prompt-table">
          <!-- Query/Prompt Column -->
          <ng-container matColumnDef="prompt">
            <th mat-header-cell *matHeaderCellDef>Query/Prompt</th>
            <td mat-cell *matCellDef="let prompt" class="prompt-cell">
              <a (click)="openPromptDetail(prompt)" class="prompt-link">{{ prompt.prompt_text }}</a>
            </td>
          </ng-container>

          <!-- Volume Column -->
          <ng-container matColumnDef="volume">
            <th mat-header-cell *matHeaderCellDef>Volume</th>
            <td mat-cell *matCellDef="let prompt">
              {{ prompt.volume ? prompt.volume.toLocaleString() : '-' }}
            </td>
          </ng-container>

          <!-- Mentions Column -->
          <ng-container matColumnDef="mentions">
            <th mat-header-cell *matHeaderCellDef>Mentions</th>
            <td mat-cell *matCellDef="let prompt">
              <span class="mentions-badge">{{ prompt.mentions }}</span>
            </td>
          </ng-container>

          <!-- Citations Column -->
          <ng-container matColumnDef="citations">
            <th mat-header-cell *matHeaderCellDef>Citations</th>
            <td mat-cell *matCellDef="let prompt" class="citations-cell">
              <mat-icon class="citation-icon">link</mat-icon>
              <span>{{ prompt.citations }}</span>
            </td>
          </ng-container>

          <!-- Updated Column -->
          <ng-container matColumnDef="updated">
            <th mat-header-cell *matHeaderCellDef>Updated</th>
            <td mat-cell *matCellDef="let prompt">
              {{ getTimeAgo(prompt.updated_at) }}
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="['prompt', 'volume', 'mentions', 'citations', 'updated']"></tr>
          <tr mat-row *matRowDef="let row; columns: ['prompt', 'volume', 'mentions', 'citations', 'updated']" 
              class="prompt-row"></tr>
        </table>

        <!-- Pagination -->
        @if (getTotalPages() > 1) {
          <div class="pagination">
            <button mat-button [disabled]="currentPage === 1" (click)="currentPage = currentPage - 1; cdr.detectChanges()">
              Previous
            </button>
            <span class="page-info">Page {{ currentPage }} of {{ getTotalPages() }}</span>
            <button mat-button [disabled]="currentPage === getTotalPages()" (click)="currentPage = currentPage + 1; cdr.detectChanges()">
              Next
            </button>
          </div>
        }
      </div>
    } @else if (!loading && !initializing && !error) {
      <div class="empty-state">
        <mat-icon class="empty-icon">track_changes</mat-icon>
        <h3>No Prompt Tracking Data</h3>
        <p>Start tracking by performing an initial analysis to collect prompt data.</p>
        <!-- <button mat-raised-button color="primary" (click)="performInitialAnalysis()">
          <mat-icon>play_arrow</mat-icon>
          <span>Start Initial Analysis</span>
        </button> -->
        <button mat-raised-button color="primary" (click)="openPromptTrackingSourceDialog()">
          <mat-icon>play_arrow</mat-icon>
          <span>Get started</span>
        </button>

      </div>
    }
  }
</div>
